import React from 'react';
import styled, { css } from 'styled-components';

import MyMatchDynamicBackground, {
  MyMatchDynamicBackgroundLeftImgClassName,
  MyMatchDynamicBackgroundRadialClassName,
  MyMatchDynamicBackgroundRightImgClassName,
} from '../../../../components/MyMatchDynamicBackground';
import { ON_MY_MATCH_TOGGLE_TRANSITION_DURATION } from '../../../../constants/ui/mymatch';
import theme from '../../../../styled-components/dark';

const Wrapper = styled.div``;

const backgroundColorCss = css`
  transition: stop-color ${ON_MY_MATCH_TOGGLE_TRANSITION_DURATION}ms ease-in-out;
`;

type SvgElementType = {
  $selected: boolean;
  $isMyMatch: boolean;
};

const SvgElement = styled.svg<SvgElementType>`
  width: 100%;
  height: 100%;

  ${({ $selected, $isMyMatch }) => {
    const defaultStyle = css`
      --card-background: ${theme.LineUpPlayeCardIcon.default.backgroundColor};
      --card-background-gradient-1: ${theme.LineUpPlayeCardIcon.default.backgroundGradient1};
      --card-background-gradient-2: ${theme.LineUpPlayeCardIcon.default.backgroundGradient2};
      --card-details-background: ${theme.LineUpPlayeCardIcon.default.detailsBackgroundColor};
      --card-light-gradient: ${theme.LineUpPlayeCardIcon.default.lightColor};
      --card-lighter-gradient: ${theme.LineUpPlayeCardIcon.default.lighterColor};
    `;

    const myMatchStyle = css`
      --card-background: black;
      --card-background-gradient-1: black;
      --card-background-gradient-2: black;
      --card-details-background: transparent;
      --card-light-gradient: transparent;
      --card-lighter-gradient: transparent;
    `;

    if ($selected) {
      const selectedBase = css`
        --outline-path-stroke: ${theme.LineUpPlayeCardIcon.default.selectedBorderColor};
        --outline-path-stroke-opacity: 1;
        --outline-path-stroke-width: 2;
      `;

      if ($isMyMatch) {
        return css`
          ${selectedBase}
          ${myMatchStyle}
        `;
      }

      return css`
        ${selectedBase}
        ${defaultStyle}
      `;
    }

    const notSelectedBase = css`
      --outline-path-stroke: url(#line-up-card-linear-outline);
      --outline-path-stroke-opacity: 0.4;
      --outline-path-stroke-width: 1;
    `;

    if ($isMyMatch) {
      return css`
        ${notSelectedBase}
        ${myMatchStyle}
      `;
    }

    return css`
      ${notSelectedBase}
      ${defaultStyle}
    `;
  }}
`;

const Mask = styled.mask`
  mask-type: alpha;
`;

const OutlinePath = styled.path`
  stroke: var(--outline-path-stroke);
  stroke-opacity: var(--outline-path-stroke-opacity);
  stroke-width: var(--outline-path-stroke-width);
`;

const StopBackground = styled.stop`
  ${backgroundColorCss}
  stop-color: var(--card-background);
`;

const StopGrandient1 = styled.stop`
  ${backgroundColorCss}
  stop-color: var(--card-background-gradient-1);
`;

const StopGrandient2 = styled.stop`
  ${backgroundColorCss}
  stop-color: var(--card-background-gradient-2);
`;

const StopLight = styled.stop`
  ${backgroundColorCss}
  stop-color: var(--card-light-gradient);
`;

const StopLighter = styled.stop`
  ${backgroundColorCss}
  stop-color: var(--card-lighter-gradient);
`;

const StopLightBackground = styled.stop`
  ${backgroundColorCss}
  stop-color: var(--card-details-background);
`;

type StyledMyMatchDynamicBackgroundType = {
  $isMyMatch: boolean;
  $selected: boolean;
};

const StyledMyMatchDynamicBackground = styled(MyMatchDynamicBackground)<StyledMyMatchDynamicBackgroundType>`
  position: absolute;
  clip-path: url(#line-up-player-card-clip-path);
  border-radius: 1.8em;

  ${({ $isMyMatch, $selected }) => {
    return css`
      transition: z-index ${ON_MY_MATCH_TOGGLE_TRANSITION_DURATION}ms ease-in-out;
      z-index: ${$isMyMatch ? 0 : -1};
      top: ${$selected ? 2 : 1}px;
      left: ${$selected ? 2 : 1}px;
      width: calc(100% - ${$selected ? 4 : 2}px);
      height: ${$selected ? 'calc(94% - 1px)' : '94%'};
    `;
  }}

  & .${MyMatchDynamicBackgroundRightImgClassName} {
    top: -50%;
    right: -100%;
    width: 400%;
  }

  & .${MyMatchDynamicBackgroundRadialClassName} {
    top: -40%;
    left: -30%;
    width: 800%;
  }

  & .${MyMatchDynamicBackgroundLeftImgClassName} {
    display: none;
  }
`;

type LineUpRugbyCardIconSvgAsComponentProps = {
  className?: string;
  selected?: boolean;
  isMyMatch?: boolean;
};

const LineUpRugbyCardIconSvgAsComponent = ({
  className = '',
  selected = false,
  isMyMatch = false,
}: LineUpRugbyCardIconSvgAsComponentProps) => {
  return (
    <Wrapper className={className}>
      <StyledMyMatchDynamicBackground isActive={isMyMatch} $isMyMatch={isMyMatch} $selected={selected} />
      <SvgElement
        $selected={selected}
        width="68"
        height="84"
        viewBox="0 0 68 84"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        $isMyMatch={isMyMatch}
      >
        <Mask id="line-up-rugby-card-mask">
          <path
            d="M15 1H53C60.732 1 67 7.26801 67 15V61.5453C67 67.5113 63.2192 72.8218 57.5818 74.7743L38.5818 81.3548C35.6138 82.3828 32.3862 82.3828 29.4182 81.3548L10.4182 74.7743C4.78077 72.8218 1 67.5113 1 61.5453V15C1 7.26802 7.26801 1 15 1Z"
            fill="#0A0B0F"
          />
        </Mask>
        <g mask="url(#line-up-rugby-card-mask)">
          <path
            d="M0 15C0 6.71573 6.71573 0 15 0H53C61.2843 0 68 6.71573 68 15V61.5453C68 67.9374 63.9492 73.6273 57.9091 75.7192L38.9091 82.2998C35.729 83.4012 32.271 83.4012 29.0909 82.2998L10.0909 75.7192C4.05082 73.6273 0 67.9374 0 61.5453V15Z"
            fill="url(#paint0_linear_3997_78101)"
          />
          <g opacity="0.5">
            <path
              d="M28 30C35.6 26.8 63.1667 16.6667 76 12V-11C72 -10.2 47.3333 -1.33333 35.5 3L38 5L31 6C29.3333 7.33333 25.2 10.5 22 12.5C18 13 15 14.5 10.5 17.5C8.5 26.3 -9.33333 32.8333 -18 35C-29 37.5 -32 40 -37.5 46C-33.5 43.5 -31 42 -26.5 40.5C-33.3 50.5 -55.3333 59.6667 -65.5 63H-16.5C-10.9 57.4 19.1667 45.3333 33.5 40C28 41 16.5 43.5 4 49.5C9.5 44.5 39 32 55.5 24.5C48 26 12.5 38.5 -8.5 47C-4.9 44.6 24.6667 32.6667 39 27C37.5 27.3333 33.2 28.4 28 30Z"
              fill="url(#line-up-card-linear-detail-1)"
            />
            <path
              d="M57.5 35C59.9 33 70.8333 28.5 76 26.5V28.5C73.5 29.5 63.5 33 57.5 35Z"
              fill="url(#line-up-card-linear-detail-2)"
            />
            <path
              d="M41 63C47.8 54.6 66.8333 42.5 75.5 37.5V39.5C64.3 45.5 52.5 57.6667 48 63H41Z"
              fill="url(#line-up-card-linear-detail-3)"
            />
            <path
              d="M-63 54.5C-45.5 42.5 3.5 13 24 3.5C-0.5 17.5 -48.5 50.6667 -63 64V54.5Z"
              fill="url(#line-up-card-linear-detail-4)"
            />

            <path d="M50 10.5L76 -3V0.5L50 10.5Z" fill="url(#line-up-card-linear-detail-5)" />
          </g>
        </g>
        <OutlinePath d="M15 1H53C60.732 1 67 7.26801 67 15V61.5453C67 67.5113 63.2192 72.8218 57.5818 74.7743L38.5818 81.3548C35.6138 82.3828 32.3862 82.3828 29.4182 81.3548L10.4182 74.7743C4.78077 72.8218 1 67.5113 1 61.5453V15C1 7.26802 7.26801 1 15 1Z" />
        <defs>
          <linearGradient
            id="paint0_linear_3997_78101"
            x1="31.5"
            y1="80.2667"
            x2="57.6096"
            y2="5.3517"
            gradientUnits="userSpaceOnUse"
          >
            <StopBackground />
            <StopGrandient1 offset="0.619405" />
            <StopGrandient2 offset="1" />
          </linearGradient>
          <linearGradient
            id="line-up-card-linear-outline"
            x1="34"
            y1="0"
            x2="34"
            y2="84"
            gradientUnits="userSpaceOnUse"
          >
            <stop offset="0.502125" stopColor="white" stopOpacity="0" />
            <stop offset="0.759638" stopColor="white" stopOpacity="0.2" />
            <stop offset="0.893924" stopColor="white" stopOpacity="0.75" />
            <stop offset="1" stopColor="white" />
          </linearGradient>
          <linearGradient
            id="line-up-card-linear-detail-1"
            x1="76"
            y1="-0.999995"
            x2="-66.5"
            y2="64"
            gradientUnits="userSpaceOnUse"
          >
            <StopLight />
            <StopBackground offset="1" stopOpacity="0" />
          </linearGradient>
          <linearGradient
            id="line-up-card-linear-detail-2"
            x1="76"
            y1="27.6486"
            x2="59.8272"
            y2="36.0856"
            gradientUnits="userSpaceOnUse"
          >
            <StopLight />
            <StopBackground offset="1" stopOpacity="0" />
          </linearGradient>
          <linearGradient
            id="line-up-card-linear-detail-3"
            x1="71"
            y1="40"
            x2="40.2454"
            y2="53.2653"
            gradientUnits="userSpaceOnUse"
          >
            <StopLight />
            <StopBackground offset="1" stopOpacity="0.2" />
          </linearGradient>
          <linearGradient
            id="line-up-card-linear-detail-4"
            x1="24"
            y1="11.6757"
            x2="-69.2819"
            y2="38.0513"
            gradientUnits="userSpaceOnUse"
          >
            <StopLight />
            <StopBackground offset="1" stopOpacity="0.2" />
          </linearGradient>
          <linearGradient
            id="line-up-card-linear-detail-5"
            x1="61"
            y1="4.5"
            x2="75.2023"
            y2="-3.00883"
            gradientUnits="userSpaceOnUse"
          >
            <StopLighter />
            <StopLightBackground offset="1" stopOpacity="0" />
          </linearGradient>
          <clipPath
            id="line-up-player-card-clip-path"
            clipPathUnits="objectBoundingBox"
            transform="scale(0.0142851, 0.01)"
          >
            <path d="M0 15C0 6.71575 6.71573 0 15 0H55C63.2843 0 70 6.71573 70 15V75.8308C70 81.9619 66.2688 87.4758 60.5773 89.7554L40.5773 97.7661C36.9973 99.2 33.0027 99.2 29.4227 97.7661L9.42272 89.7554C3.73123 87.4758 0 81.9619 0 75.8308V15Z" />
          </clipPath>
        </defs>
      </SvgElement>
    </Wrapper>
  );
};

export default React.memo(LineUpRugbyCardIconSvgAsComponent);
